import { useState, FormEvent } from 'react';
import { useCelebration, SpecialDay, CelebrationTheme } from '@/context/CelebrationContext';
import useScrollLock from '@/hooks/useScrollLock';

interface Props {
  day: SpecialDay | null;
  dark: boolean;
  onClose: () => void;
}

const today = () => new Date().toISOString().split('T')[0];
const nextWeek = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().split('T')[0];
};

const THEMES: { value: CelebrationTheme; label: string; icon: string; preview: string }[] = [
  { value: 'confetti',   label: 'Confetti',   icon: 'ri-gift-line',        preview: '■▲●◆' },
  { value: 'fireworks',  label: 'Fireworks',  icon: 'ri-fire-line',        preview: '★✦✧✶' },
  { value: 'snowflakes', label: 'Snowflakes', icon: 'ri-snowy-line',       preview: '❄❅❆✻' },
  { value: 'hearts',     label: 'Hearts',     icon: 'ri-heart-line',       preview: '♥♡❤💕' },
  { value: 'stars',      label: 'Stars',      icon: 'ri-star-line',        preview: '★☆✦✧' },
  { value: 'waves',      label: 'Waves',      icon: 'ri-water-flash-line', preview: '〰≈∿〜' },
];

const PRESET_COLORS = [
  '#F97316', '#EF4444', '#EC4899', '#8B5CF6',
  '#3B82F6', '#10B981', '#F59E0B', '#06B6D4',
];

const PRESET_EMOJIS = ['🎉', '🎊', '🎁', '🎄', '🎆', '🎇', '⛵', '🌊', '🏖️', '🐟', '❄️', '💙', '🌟', '🔥', '🎈', '🥳'];

export default function SpecialDayFormModal({ day, dark, onClose }: Props) {
  const { addSpecialDay, updateSpecialDay } = useCelebration();
  const isEdit = !!day;
  useScrollLock();

  const [form, setForm] = useState<Omit<SpecialDay, 'id'>>({
    name: day?.name || '',
    message: day?.message || '',
    subMessage: day?.subMessage || '',
    theme: day?.theme || 'confetti',
    themeColor: day?.themeColor || '#F97316',
    emoji: day?.emoji || '🎉',
    active: day?.active ?? true,
    startDate: day?.startDate || today(),
    endDate: day?.endDate || nextWeek(),
    showBanner: day?.showBanner ?? true,
    dismissible: day?.dismissible ?? true,
  });

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (isEdit && day) {
      updateSpecialDay({ ...form, id: day.id });
    } else {
      addSpecialDay(form);
    }
    onClose();
  };

  const inputCls = dark
    ? 'bg-[#0D1B2A] border-white/10 text-white placeholder-white/30 focus:border-[#F97316]'
    : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#F97316]';
  const labelCls = `block text-xs font-semibold uppercase tracking-wide mb-1.5 ${dark ? 'text-white/60' : 'text-gray-500'}`;
  const fieldCls = `w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors ${inputCls}`;
  const text = dark ? 'text-white' : 'text-gray-900';
  const muted = dark ? 'text-white/50' : 'text-gray-500';
  const borderCls = dark ? 'border-white/10' : 'border-gray-100';
  const sectionBg = dark ? 'bg-[#0D1B2A]/60' : 'bg-gray-50';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className={`w-full max-w-xl max-h-[92vh] overflow-y-auto rounded-xl ${dark ? 'bg-[#1C2B3A]' : 'bg-white'}`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${borderCls}`}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg text-xl">
              {form.emoji}
            </div>
            <h2 className={`font-bold text-base ${text}`}>{isEdit ? 'Edit Special Day' : 'New Special Day'}</h2>
          </div>
          <button onClick={onClose} className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer ${dark ? 'text-white/50 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'}`}>
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">

          {/* Name */}
          <div>
            <label className={labelCls}>Event Name *</label>
            <input required className={fieldCls} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="New Year 2026, Summer Sale, Black Friday..." />
          </div>

          {/* Message */}
          <div>
            <label className={labelCls}>Banner Message *</label>
            <input required className={fieldCls} value={form.message} onChange={(e) => set('message', e.target.value)} placeholder="Happy New Year! Enjoy 20% off all batteries" />
          </div>

          {/* Sub message */}
          <div>
            <label className={labelCls}>Sub Message (optional)</label>
            <input className={fieldCls} value={form.subMessage} onChange={(e) => set('subMessage', e.target.value)} placeholder="Limited time offer — valid through Jan 3rd" />
          </div>

          {/* Emoji picker */}
          <div>
            <label className={labelCls}>Emoji</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {PRESET_EMOJIS.map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => set('emoji', e)}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg text-lg transition-all cursor-pointer border ${
                    form.emoji === e
                      ? 'border-[#F97316] bg-[#F97316]/10'
                      : dark ? 'border-white/10 hover:border-white/20' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {e}
                </button>
              ))}
              <input
                className={`${fieldCls} w-20`}
                value={form.emoji}
                onChange={(e) => set('emoji', e.target.value)}
                placeholder="✨"
                maxLength={4}
              />
            </div>
          </div>

          {/* Theme */}
          <div className={`rounded-lg p-4 border ${borderCls} ${sectionBg}`}>
            <p className={`text-xs font-bold uppercase tracking-widest mb-3 ${muted}`}>
              <i className="ri-magic-line mr-1.5 text-[#F97316]"></i>
              Animation Theme
            </p>
            <div className="grid grid-cols-3 gap-2">
              {THEMES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => set('theme', t.value)}
                  className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-lg text-xs font-semibold transition-all cursor-pointer border ${
                    form.theme === t.value
                      ? 'border-[#F97316] bg-[#F97316]/10 text-[#F97316]'
                      : dark ? 'border-white/10 text-white/50 hover:border-white/20 hover:text-white' : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-900'
                  }`}
                >
                  <i className={`${t.icon} text-lg`}></i>
                  <span>{t.label}</span>
                  <span className="text-base tracking-widest">{t.preview}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Accent color */}
          <div>
            <label className={labelCls}>Accent Color</label>
            <div className="flex items-center gap-3 flex-wrap">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => set('themeColor', c)}
                  className={`w-8 h-8 rounded-full cursor-pointer transition-all border-2 ${form.themeColor === c ? 'border-white scale-110' : 'border-transparent'}`}
                  style={{ background: c }}
                />
              ))}
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.themeColor}
                  onChange={(e) => set('themeColor', e.target.value)}
                  className="w-8 h-8 rounded-full cursor-pointer border-0 bg-transparent"
                />
                <span className={`text-xs font-mono ${muted}`}>{form.themeColor}</span>
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Start Date *</label>
              <input type="date" required className={fieldCls} value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>End Date *</label>
              <input type="date" required className={fieldCls} value={form.endDate} onChange={(e) => set('endDate', e.target.value)} />
            </div>
          </div>

          {/* Options */}
          <div className={`rounded-lg p-4 border ${borderCls} ${sectionBg} space-y-3`}>
            <p className={`text-xs font-bold uppercase tracking-widest mb-1 ${muted}`}>Options</p>
            {[
              { key: 'active' as const, label: 'Active', desc: 'Enable this special day' },
              { key: 'showBanner' as const, label: 'Show Banner', desc: 'Display banner on homepage' },
              { key: 'dismissible' as const, label: 'Dismissible', desc: 'Allow visitors to close the banner' },
            ].map((opt) => (
              <div key={opt.key} className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${text}`}>{opt.label}</p>
                  <p className={`text-xs ${muted}`}>{opt.desc}</p>
                </div>
                <button
                  type="button"
                  onClick={() => set(opt.key, !form[opt.key])}
                  className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${form[opt.key] ? 'bg-[#F97316]' : dark ? 'bg-white/20' : 'bg-gray-200'}`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${form[opt.key] ? 'left-5' : 'left-0.5'}`}></span>
                </button>
              </div>
            ))}
          </div>

          {/* Live preview */}
          <div className={`rounded-lg overflow-hidden border ${borderCls}`}>
            <p className={`text-xs font-bold uppercase tracking-widest px-4 py-2 ${sectionBg} ${muted} border-b ${borderCls}`}>
              Banner Preview
            </p>
            <div
              className="px-5 py-3 flex items-center gap-4"
              style={{ background: `linear-gradient(135deg, ${form.themeColor}22 0%, ${form.themeColor}11 50%, ${form.themeColor}22 100%)`, borderBottom: `2px solid ${form.themeColor}44` }}
            >
              <span className="text-2xl">{form.emoji}</span>
              <div className="flex-1 min-w-0">
                <p className="font-black text-sm truncate" style={{ color: form.themeColor }}>{form.message || 'Your message here...'}</p>
                {form.subMessage && <p className={`text-xs mt-0.5 truncate ${muted}`}>{form.subMessage}</p>}
              </div>
              <span className="text-xs font-bold px-3 py-1 rounded-full text-white whitespace-nowrap" style={{ background: form.themeColor }}>
                Explore Products
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className={`flex gap-3 pt-2 border-t ${borderCls}`}>
            <button type="button" onClick={onClose} className={`flex-1 py-2.5 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap border transition-colors ${dark ? 'border-white/20 text-white/70 hover:bg-white/10' : 'border-gray-200 text-gray-600 hover:bg-gray-100'}`}>
              Cancel
            </button>
            <button type="submit" className="flex-1 py-2.5 rounded-lg text-sm font-bold bg-[#F97316] text-white hover:bg-[#ea6a0a] transition-colors cursor-pointer whitespace-nowrap">
              {isEdit ? 'Save Changes' : 'Create Special Day'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
